import { HandCoins, HelpCircle, Download, Loader2 } from 'lucide-react';
import { useIsMobile } from '@hooks/useIsMobile';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { formatPrice } from '../../../../utils/priceHelpers';
import { ContractsTable, type ContractsTableTotals } from './ContractsTable';

export interface FinancialSummary {
  totalSales: number;
  totalReceived: number;
  totalRemaining: number;
  vendorTotalExpected: number;
  vendorTotalReceived: number;
  vendorTotalRemaining: number;
  feesPaid: number;
  feesRemaining: number;
  savTotalBeneficiary: number;
  savTotalSeller: number;
  savTotalCommercial: number;
  savGrandTotal: number;
}

interface Pagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

interface RecapViewProps {
  summary: FinancialSummary;
  filteredContracts?: any[];
  totals?: ContractsTableTotals;
  hasChildren?: boolean;
  societyId?: string;
  vendorName?: string;
  pagination?: Pagination;
  onPageChange?: (page: number) => void;
  onExport?: () => void;
  exporting?: boolean;
  search?: string;
  onSearchChange?: (value: string) => void;
  paymentMode?: string;
  onPaymentModeChange?: (value: string) => void;
  monthlyPayment?: number;
  onlinePayment?: number;
}

const Tooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="tw-group tw-relative tw-inline-flex">
    <HelpCircle size={14} className="tw-text-gray-400 tw-cursor-help" />
    <span className="tw-absolute tw-left-1/2 tw--translate-x-1/2 tw-bottom-full tw-mb-2 tw-px-3 tw-py-2 tw-bg-gray-900 tw-text-white tw-text-xs tw-rounded-lg tw-w-64 tw-max-w-[90vw] tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-pointer-events-none tw-z-50 tw-font-normal tw-normal-case">
      {text}
    </span>
  </span>
);

const pct = (part: number, total: number) =>
  total > 0 ? ((part / total) * 100).toFixed(1) : '0.0';

export const RecapView: React.FC<RecapViewProps> = ({
  summary,
  filteredContracts,
  totals,
  hasChildren = false,
  societyId = '',
  vendorName = '',
  pagination,
  onPageChange,
  onExport,
  exporting = false,
  search = '',
  onSearchChange,
  paymentMode = '',
  onPaymentModeChange,
  monthlyPayment,
  onlinePayment,
}) => {
  const isMobile = useIsMobile();
  const pieRadius = isMobile ? 55 : 75;
  const totalFees = (summary.feesPaid || 0) + (summary.feesRemaining || 0);

  const rows = [
    {
      label: 'Votre facturation client',
      tooltip: 'Montants facturés aux clients (bénéficiaires). Total facturé = somme des prix TTC des contrats. Payé = montant déjà réglé par le client. À payer = reste dû par le client. Un contrat annulé compte pour ce que le client a payé, remboursements déduits. Les paiements contestés (litiges) sont déduits.',
      total: summary.totalSales,
      paid: summary.totalReceived,
      remaining: summary.totalRemaining,
      totalLabel: 'Total',
      paidLabel: 'Payé',
      remainingLabel: 'Restant',
      colorClass: 'tw-text-affinicar-primary',
      fillColor: '#8074af',
      borderClass: 'tw-border-l-4 tw-border-l-affinicar-primary',
    },
    {
      label: 'Vos encaissements',
      tooltip: 'Montants reversés à la société (hors frais de transaction). Total à encaisser = prix catalogue des garanties. Encaissé = montant déjà versé. Reste à encaisser = montant en attente de versement. Un contrat annulé compte pour ce qui a été versé : remboursements client déduits, commissions rendues ajoutées. Les montants retirés par un litige sont déduits.',
      total: summary.vendorTotalExpected,
      paid: summary.vendorTotalReceived,
      remaining: summary.vendorTotalRemaining,
      totalLabel: 'À recevoir',
      paidLabel: 'Reçu',
      remainingLabel: 'Restant',
      colorClass: 'tw-text-green-700',
      fillColor: '#15803d',
      borderClass: 'tw-border-l-4 tw-border-l-green-500',
    },
    {
      label: 'Vos frais de transactions',
      tooltip: 'Frais de transaction Stripe prélevés sur les paiements. Total = somme de tous les frais. Payé = frais déjà prélevés. À payer = frais restants à prélever sur les échéances futures. Les commissions rendues par avoir sont déduites.',
      total: totalFees,
      paid: summary.feesPaid,
      remaining: summary.feesRemaining,
      totalLabel: 'Total',
      paidLabel: 'Payé',
      remainingLabel: 'Restant',
      colorClass: 'tw-text-red-600',
      fillColor: '#dc2626',
      borderClass: 'tw-border-l-4 tw-border-l-red-500',
    },
  ];

  return (
    <div className="tw-space-y-4 tw-pb-6">
      {/* Bandeau titre */}
      <div className="tw-flex tw-items-center tw-justify-between tw-bg-affinicar-primary tw-text-white tw-uppercase tw-font-bold tw-px-4 tw-py-4 tw-rounded-lg tw-min-h-[56px]">
        <div className="tw-flex tw-items-center tw-gap-2">
          <HandCoins className="tw-w-4 sm:tw-w-5 tw-h-4 sm:tw-h-5 tw-shrink-0" />
          <span className="tw-text-sm sm:tw-text-base tw-font-bold tw-leading-none">Suivi des encaissements TTC</span>
        </div>
        {onExport && (
          <button
            onClick={onExport}
            disabled={exporting}
            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-white/20 tw-rounded-md tw-text-sm tw-font-medium tw-normal-case hover:tw-bg-white/30 tw-transition-colors disabled:tw-opacity-50"
          >
            {exporting ? <Loader2 size={14} className="tw-animate-spin" /> : <Download size={14} />}
            <span className="tw-hidden sm:tw-inline">Export</span>
          </button>
        )}
      </div>

      {/* Filtres spécifiques */}
      {(onSearchChange || onPaymentModeChange) && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3">
          {onSearchChange && (
            <input
              type="text"
              placeholder="N° de contrat, client..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="tw-px-3 tw-py-1.5 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary tw-w-full sm:tw-w-40"
            />
          )}
          {onPaymentModeChange && (
            <select
              value={paymentMode}
              onChange={(e) => onPaymentModeChange(e.target.value)}
              className="tw-px-3 tw-py-1.5 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary tw-w-full sm:tw-w-auto"
            >
              <option value="">Tous les modes</option>
              {onlinePayment === 2 && <option value="Paiement comptant">Comptant Stripe</option>}
              <option value="Paiement comptant agence">Comptant</option>
              {monthlyPayment === 2 && <option value="Paiement mensualisé">Mensualisé</option>}
            </select>
          )}
        </div>
      )}

      {/* Graphiques camembert synthèse */}
      <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-2">Synthèse</h3>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
        {rows.map((row) => {
          const pieData = [
            { name: row.paidLabel, value: Math.max(0, row.paid || 0) },
            { name: row.remainingLabel, value: Math.max(0, row.remaining || 0) },
          ];
          const hasData = (row.paid || 0) > 0 || (row.remaining || 0) > 0;

          return (
            <div key={row.label} className={`tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 ${row.borderClass}`}>
              <div className={`tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-font-semibold tw-text-sm ${row.colorClass}`}>
                {row.label}
                <Tooltip text={row.tooltip} />
              </div>

              {hasData ? (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      innerRadius={0}
                      outerRadius={pieRadius}
                      dataKey="value"
                      paddingAngle={2}
                    >
                      <Cell fill={row.fillColor} />
                      <Cell fill="#f97316" />
                    </Pie>
                    <RechartsTooltip
                      formatter={(value: number) => formatPrice(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="tw-flex tw-items-center tw-justify-center tw-h-[180px] tw-text-gray-400 tw-text-sm">
                  Aucune donnée
                </div>
              )}

              <div className="tw-space-y-1 tw-text-sm tw-mt-2">
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">{row.totalLabel}</span>
                  <span className="tw-font-bold tw-text-gray-900">{formatPrice(row.total || 0)}</span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">{row.paidLabel} ({pct(row.paid, row.total)}%)</span>
                  <span className={`tw-font-bold ${row.colorClass}`}>{formatPrice(row.paid || 0)}</span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">{row.remainingLabel}</span>
                  <span className={`tw-font-bold ${(row.remaining || 0) > 0 ? 'tw-text-orange-600' : 'tw-text-gray-500'}`}>{formatPrice(row.remaining || 0)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tableau des contrats */}
      {filteredContracts && totals && (<>
        <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-2">
          Détail par contrat
          {pagination && <span className="tw-text-sm tw-font-normal tw-text-gray-500 tw-ml-2">({pagination.totalCount} contrats)</span>}
        </h3>
        <ContractsTable
          filteredContracts={filteredContracts}
          totals={totals}
          hasChildren={hasChildren}
          societyId={societyId}
          vendorName={vendorName}
        />
        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && onPageChange && (
          <div className="tw-flex tw-items-center tw-justify-between tw-mt-4 tw-px-2">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Page {pagination.page + 1} sur {pagination.totalPages}
            </p>
            <div className="tw-flex tw-gap-2">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
              >
                Précédent
              </button>
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </>)}
    </div>
  );
};
